import clsx from "clsx";
import { Fragment, useEffect, useState } from "react";

import { LinkedInLogin } from "common/components/LinkedInLogin";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
} from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { CampaignOutreachType, CampaignType } from "common/types";

import CampaignSettings from "./CampaignSettings";
import CampaignTypes from "./CampaignTypes";

import ConnectAccountImage from "assets/images/empty-placeholders/connect-account.png";

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateCampaignModal({
  isOpen,
  onClose,
}: CreateCampaignModalProps) {
  const { accounts_count: accountsCount, permissions } =
    useSelectedWorkspaceContext();
  const isDesktop = useTwBreakpoint("lg");

  const hasAccounts = accountsCount > 0;
  const initialStepIndex = hasAccounts ? 2 : 0;
  const initialType: CampaignType = "search";
  const initialOutreachType: CampaignOutreachType = "connect";

  const [stepIndex, setStepIndex] = useState(initialStepIndex);
  const [campaignType, setCampaignType] = useState<CampaignType>(initialType);
  const [outreachType, setOutreachType] =
    useState<CampaignOutreachType>(initialOutreachType);

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;

  function resetState() {
    setStepIndex(initialStepIndex);
    setCampaignType(initialType);
    setOutreachType(initialOutreachType);
  }

  // When the modal (re)opens, resync the starting step with the latest accountsCount
  useEffect(() => {
    if (isOpen) {
      setStepIndex(initialStepIndex);
    }
  }, [isOpen, accountsCount]);

  const steps = [
    <Fragment key={0}>
      <img
        src={ConnectAccountImage}
        width={364}
        height={254}
        alt=""
        className="mx-auto mb-4"
      />
      <h3 className="mb-2 text-center text-2xl-bold lg:text-xl-bold">
        Connect a LinkedIn Account to create a campaign
      </h3>
      <p className="mb-8 text-center text-sm text-black-500">
        Campaigns are run from individual LinkedIn accounts. Add a LinkedIn
        account to create a campaign
      </p>
      <div className="flex flex-col gap-2 lg:flex-row-reverse lg:gap-4">
        <Button className="flex-1" size="lg" onClick={() => setStepIndex(1)}>
          Add account
        </Button>
        <Close asChild>
          <Button className="flex-1" size="lg" variant="secondary-black">
            Cancel
          </Button>
        </Close>
      </div>
    </Fragment>,
    <LinkedInLogin
      key={1}
      onSuccess={() => {
        if (permissions.includes("campaigns_full")) {
          setStepIndex(2);
        } else {
          onClose();
        }
      }}
    />,
    <CampaignTypes
      key={2}
      type={campaignType}
      setType={setCampaignType}
      outreachType={outreachType}
      setOutreachType={setOutreachType}
      goToNextStep={() => setStepIndex(3)}
    />,
    <CampaignSettings
      key={3}
      type={campaignType}
      goBack={() => setStepIndex(2)}
      outreachType={outreachType}
      setOutreachType={setOutreachType}
      resetState={resetState}
    />,
  ];

  return (
    <Component
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      onAfterClose={resetState}
    >
      <Content
        className={clsx("transition-none", stepIndex === 2 && "lg:w-[640px]")}
      >
        {steps[stepIndex]}
      </Content>
    </Component>
  );
}
